// src/pages/RegisterViaCodePage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Stack,
  Divider,
} from '@mui/material';
import { Visibility, VisibilityOff, Key, Person, Email } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  resolveUniversalRegCode,
  registerViaUniversalRegCode,
} from '../../services/universalRegCodesService';

const RegisterViaCodePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialCodeFromUrl = useMemo(
    () => (searchParams.get('code') || '').trim(),
    [searchParams]
  );

  const [code, setCode] = useState(initialCodeFromUrl);

  // stage control
  const [stage, setStage] = useState('code'); // 'code' | 'form'

  // resolved data
  const [isResolving, setIsResolving] = useState(false);
  const [resolved, setResolved] = useState(null);
  const [resolveError, setResolveError] = useState(null);

  // form fields
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // submit state
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(null);

  const resolvedEmail = resolved?.email || '';
  const expiresAt = resolved?.expires_at || null;

  // If code came from URL, you can optionally auto-focus but still require click.
  useEffect(() => {
    if (initialCodeFromUrl) {
      setStage('code');
    }
  }, [initialCodeFromUrl]);

  const resetToCodeStage = () => {
    setStage('code');
    setResolved(null);
    setResolveError(null);
    setErr(null);
    setSuccess(null);
    setUsername('');
    setPassword('');
    setPassword2('');
  };

  const handleResolve = async (e) => {
    e.preventDefault();
    setErr(null);
    setSuccess(null);
    setResolveError(null);
    setResolved(null);

    const trimmed = (code || '').trim();
    if (!trimmed) {
      setResolveError(t('errorMessages.fillInAllFields', 'Please fill in all fields.'));
      return;
    }
    if (trimmed.length < 6) {
      setResolveError(t('register.invalid_code', 'Code looks too short.'));
      return;
    }

    setIsResolving(true);
    const res = await resolveUniversalRegCode({ code: trimmed });
    setIsResolving(false);

    if (!res.success) {
      setResolveError(res.error || t('register.registration_failed', 'Registration failed.'));
      return;
    }

    setResolved(res.data);

    // Suggest username from email local part (only once)
    if (!username && res.data?.email) {
      const local = String(res.data.email).split('@')[0] || '';
      if (local) setUsername(local);
    }

    setStage('form');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSuccess(null);

    const trimmedCode = (code || '').trim();
    const trimmedUsername = (username || '').trim();

    if (!trimmedCode || !trimmedUsername || !password) {
      setErr(t('errorMessages.fillInAllFields', 'Please fill in all fields.'));
      return;
    }
    if (password !== password2) {
      setErr(t('errorMessages.passwordsDoNotMatch', 'Passwords do not match.'));
      return;
    }

    setSubmitting(true);
    try {
      const res = await registerViaUniversalRegCode({
        code: trimmedCode,
        username: trimmedUsername,
        password,
      });

      if (res.success) {
        setSuccess(t('register.registration_successful', 'Registration successful.'));
        setTimeout(() => navigate('/login'), 800);
      } else {
        setErr(res.error || t('register.registration_failed', 'Registration failed.'));
      }
    } catch (e2) {
      setErr(e2.message || t('register.registration_failed', 'Registration failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>
      <Paper elevation={3} sx={{ width: '100%', maxWidth: 520, p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t('register.register_via_code', 'Register via code')}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('register.enter_registration_code', 'Enter your registration code to continue.')}
        </Typography>

        <Stack spacing={2}>
          {err && <Alert severity="error">{err}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
          {resolveError && !err && <Alert severity="warning">{resolveError}</Alert>}
        </Stack>

        {stage === 'code' ? (
          <form onSubmit={handleResolve} noValidate>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                label={t('register_form.registration_code', 'Registration code')}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Key />
                    </InputAdornment>
                  ),
                }}
              />

              <Button type="submit" variant="contained" disabled={isResolving} fullWidth>
                {isResolving ? <CircularProgress size={22} /> : t('register.continue', 'Continue')}
              </Button>

              <Button variant="text" color="secondary" fullWidth onClick={() => navigate('/login')}>
                {t('register_form.already_have_account', 'Already have an account?')}
              </Button>
            </Stack>
          </form>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <Stack spacing={2} sx={{ mt: 2 }}>
              {/* Code stays visible but read-only in stage 2 */}
              <TextField
                label={t('register_form.registration_code', 'Registration code')}
                value={code}
                fullWidth
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Key />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Resolved email */}
              {resolvedEmail ? (
                <TextField
                  label={t('register_form.email', 'Email')}
                  value={resolvedEmail}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                  }}
                  helperText={
                    expiresAt
                      ? t('register_form.expires_at', 'Code expires at') +
                        `: ${new Date(expiresAt).toLocaleString()}`
                      : undefined
                  }
                />
              ) : null}

              <Divider />

              <TextField
                label={t('register_form.username', 'Username')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label={t('register_form.password', 'Password')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                fullWidth
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((s) => !s)}
                        edge="end"
                        aria-label="toggle password visibility"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label={t('register_form.confirm_password', 'Confirm password')}
                type={showPassword ? 'text' : 'password'}
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                autoComplete="new-password"
                fullWidth
              />

              <Button type="submit" variant="contained" disabled={submitting} fullWidth>
                {submitting ? <CircularProgress size={22} /> : t('register.sign_up', 'Sign up')}
              </Button>

              <Button
                variant="text"
                color="secondary"
                fullWidth
                onClick={resetToCodeStage}
                disabled={submitting}
              >
                {t('register.change_code', 'Use a different code')}
              </Button>

              <Button
                variant="text"
                color="secondary"
                fullWidth
                onClick={() => navigate('/login')}
                disabled={submitting}
              >
                {t('register_form.already_have_account', 'Already have an account?')}
              </Button>
            </Stack>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default RegisterViaCodePage;
